import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field, ConditionFields, openDocument } from "@/components/WineFormSections";

const KEYS = ["supplier", "purchase_date", "purchase_price", "fill_level", "label_condition", "capsule_condition", "condition_other"];

export const bottleSummary = (b) => [
  b.supplier, b.purchase_date, b.purchase_price != null && b.purchase_price !== "" ? `€${Number(b.purchase_price).toFixed(2)}` : "",
  b.fill_level && `Fill: ${b.fill_level}`, b.label_condition && `Label: ${b.label_condition}`, b.capsule_condition && `Capsule: ${b.capsule_condition}`, b.condition_other,
].filter(Boolean).join(" · ");

export const BottleDetailsDialog = ({ bottles, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [init, setInit] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const first = bottles[0];
  useEffect(() => {
    if (first) {
      const common = Object.fromEntries(KEYS.map((k) => {
        const vals = new Set(bottles.map((b) => b[k] ?? ""));
        return [k, vals.size === 1 ? [...vals][0] : ""];
      }));
      setF(common);
      setInit(common);
      setFile(null);
    }
  }, [first, bottles]);
  if (!first) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ids = bottles.map((b) => b.id);
  const single = bottles.length === 1;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const changed = Object.fromEntries(KEYS.filter((k) => single || String(f[k] ?? "") !== String(init[k] ?? "")).map((k) => [k, k === "purchase_price" ? (f[k] === "" ? null : Number(f[k])) : f[k]]));
      await api.patch("/bottles/details", { ids, ...changed });
      if (file) {
        const fd = new FormData();
        fd.append("ids", ids.join(","));
        fd.append("file", file);
        await api.post("/bottles/document", fd).catch((err) => toast.error(`Details saved, but the document failed: ${errMsg(err)}`));
      }
      toast.success(single ? "Bottle updated" : `${ids.length} bottles updated`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const removeDoc = async () => {
    await api.delete(`/bottles/${first.id}/document`).catch(() => {});
    toast.success("Document removed");
    onSaved();
    onClose();
  };
  const pick = (e) => {
    const p = e.target.files[0];
    e.target.value = "";
    if (!p) return;
    if (!["application/pdf", "image/jpeg", "image/png"].includes(p.type)) return toast.error("Only PDF, JPEG or PNG files");
    if (p.size > 10 * 1024 * 1024) return toast.error("The file is larger than 10 MB");
    setFile(p);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#161214] border-[#2E252A]" data-testid="bottle-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{single ? <>Bottle <span className="font-mono text-lg">{first.barcode}</span></> : `${bottles.length} bottles`}</DialogTitle>
          {!single && <p className="text-xs text-[#6E665D]">Only the fields you change are applied to every selected bottle. Fields left blank where the bottles differ keep each bottle's own value.</p>}
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4 mt-2">
          <Field label="Supplier"><Input value={f.supplier || ""} onChange={set("supplier")} data-testid="bottle-supplier-input" /></Field>
          <Field label="Purchase date"><Input type="date" value={f.purchase_date || ""} onChange={set("purchase_date")} data-testid="bottle-purchase-date-input" /></Field>
          <Field label="Purchase price"><Input type="number" step="0.01" min="0" value={f.purchase_price ?? ""} onChange={set("purchase_price")} data-testid="bottle-purchase-price-input" /></Field>
          <Field label="Purchase document (PDF, JPEG or PNG)">
            <div className="flex items-center gap-2 h-10 rounded-md border border-dashed border-[#4A3B43] px-3 text-sm">
              {file ? <span className="flex-1 truncate text-[#F5F0EB]" data-testid="bottle-document-new">{file.name}</span>
                : single && first.purchase_document ? <button type="button" onClick={() => openDocument(null, `/bottles/${first.id}/document`)} className="flex-1 truncate text-left text-[#D4AF37]" data-testid="bottle-document-open"><FileText className="w-4 h-4 inline mr-1" />{first.purchase_document.filename}</button>
                : <span className="flex-1 text-[#6E665D]">No document</span>}
              <label className="cursor-pointer text-[#A89F91] hover:text-[#D4AF37]" aria-label="Attach document"><Upload className="w-4 h-4" /><input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={pick} data-testid="bottle-document-input" /></label>
              {single && first.purchase_document && !file && <button type="button" onClick={removeDoc} className="text-[#A89F91] hover:text-[#8C2B36]" data-testid="bottle-document-remove" aria-label="Remove document"><X className="w-4 h-4" /></button>}
            </div>
          </Field>
          <ConditionFields f={f} setF={setF} />
          <div className="col-span-2 flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="bottle-details-cancel">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="bottle-details-save">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
